import { getToken } from '@bettercomp/ams-spa-auth';
import { getAmsSession } from 'service/amsSession';
import { workspaceCodeOf } from 'service/amsToken';

const BEARER_PREFIX = 'Bearer ';

/**
 * The token to re-send a request that got a 401 with, or null to surface the 401: a renewal that
 * landed in flight, or one this 401 triggers. Never a token for another workspace than the sent
 * one: renewal re-resolves the landing, and a request must not follow it into another tenant.
 */
export const tokenForRetry = async (sentToken: string): Promise<string | null> => {
  if (getToken() === sentToken) {
    const result = await getAmsSession()?.renew({ trigger: 'unauthorized' });
    if (result?.status !== 'renewed') {
      return null;
    }
  }
  const freshToken = getToken();
  return freshToken && workspaceCodeOf(freshToken) === workspaceCodeOf(sentToken) ? freshToken : null;
};

/**
 * fetch wrapper shared by every client stack: a transparent passthrough while
 * no AMS session is live (legacy 401 semantics stay untouched), and a
 * single-retry renew-on-401 when the request carried the AMS bearer.
 * Renewal storms coalesce on RenewalPolicy's shared in-flight promise.
 */
export const fetchWithRenewal: typeof fetch = async (input, init) => {
  if (!getToken()) {
    return fetch(input, init);
  }

  // Bodies are one-shot streams: dispatch a clone so the original stays
  // readable for the single retry.
  const request = new Request(input, init);
  const sentAuth = request.headers.get('Authorization');
  const response = await fetch(request.clone());

  if (response.status !== 401 || !sentAuth?.startsWith(BEARER_PREFIX)) {
    return response;
  }

  const retryToken = await tokenForRetry(sentAuth.slice(BEARER_PREFIX.length));
  if (!retryToken) {
    return response;
  }

  request.headers.set('Authorization', `${BEARER_PREFIX}${retryToken}`);
  return fetch(request);
};
